import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { ScoreTable } from "./AbilityBoostWizardPf2e";

const ABILITIES = ["str", "dex", "con", "int", "wis", "cha"];
const ABILITY_LABELS = { str: "Strength", dex: "Dexterity", con: "Constitution", int: "Intelligence", wis: "Wisdom", cha: "Charisma" };
const STANDARD_ARRAY = [15, 14, 13, 12, 10, 8];
const POINT_BUY_COST = { 8: 0, 9: 1, 10: 2, 11: 3, 12: 4, 13: 5, 14: 7, 15: 9 };
const POINT_BUY_BUDGET = 27;

const roll4d6DropLowest = () => {
  const rolls = Array.from({ length: 4 }, () => 1 + Math.floor(Math.random() * 6)).sort((a, b) => b - a);
  return rolls[0] + rolls[1] + rolls[2];
};

// Standard Array and Roll both boil down to "assign each of these 6 values to a different ability" —
// same assignment UI, just a different starting pool of numbers.
function AssignPoolStep({ pool, onDone }) {
  const [assigned, setAssigned] = useState({});

  const availableFor = (ability) => {
    const current = assigned[ability];
    const usedCounts = {};
    for (const [k, v] of Object.entries(assigned)) if (k !== ability) usedCounts[v] = (usedCounts[v] || 0) + 1;
    const poolCounts = {};
    for (const v of pool) poolCounts[v] = (poolCounts[v] || 0) + 1;
    const options = [];
    for (const v of new Set(pool)) if ((poolCounts[v] || 0) - (usedCounts[v] || 0) > 0) options.push(v);
    if (current != null && !options.includes(current)) options.push(current);
    return options.sort((a, b) => b - a);
  };

  const allAssigned = ABILITIES.every((a) => assigned[a] != null);

  return (
    <div className="space-y-3">
      <p className="text-sm text-[#f1e0c5]/70">Assign each value to a different ability.</p>
      <div className="grid grid-cols-2 gap-2">
        {ABILITIES.map((a) => (
          <div key={a} className="flex items-center gap-2">
            <span className="text-xs w-24 text-[#f1e0c5]/60">{ABILITY_LABELS[a]}</span>
            <select
              data-testid={`assign-select-${a}`}
              value={assigned[a] ?? ""}
              onChange={(e) => setAssigned({ ...assigned, [a]: e.target.value ? parseInt(e.target.value, 10) : undefined })}
              className="bg-black/30 border border-white/10 rounded-lg text-xs px-2 py-1.5 flex-1 text-[#f1e0c5]"
            >
              <option value="">—</option>
              {availableFor(a).map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        ))}
      </div>
      <Button data-testid="assign-pool-continue-button" disabled={!allAssigned} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f]" onClick={() => onDone({ str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10, ...assigned })}>Continue</Button>
    </div>
  );
}

function PointBuyStep({ onDone }) {
  const [scores, setScores] = useState({ str: 8, dex: 8, con: 8, int: 8, wis: 8, cha: 8 });
  const spent = ABILITIES.reduce((sum, a) => sum + POINT_BUY_COST[scores[a]], 0);
  const budgetLeft = POINT_BUY_BUDGET - spent;

  const bump = (a, delta) => {
    const next = scores[a] + delta;
    if (next < 8 || next > 15) return;
    const nextSpent = spent - POINT_BUY_COST[scores[a]] + POINT_BUY_COST[next];
    if (nextSpent > POINT_BUY_BUDGET) return;
    setScores({ ...scores, [a]: next });
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-[#f1e0c5]/70" data-testid="point-buy-budget-remaining">{budgetLeft} of {POINT_BUY_BUDGET} points remaining</p>
      <div className="space-y-2">
        {ABILITIES.map((a) => (
          <div key={a} className="flex items-center gap-2">
            <span className="text-xs w-24 text-[#f1e0c5]/60">{ABILITY_LABELS[a]}</span>
            <button type="button" data-testid={`point-buy-minus-${a}`} disabled={scores[a] <= 8} onClick={() => bump(a, -1)} className="w-7 h-7 rounded-full border border-white/10 disabled:opacity-30 hover:border-[#D4AF37]">-</button>
            <span className="w-8 text-center text-[#D4AF37]">{scores[a]}</span>
            <button type="button" data-testid={`point-buy-plus-${a}`} disabled={scores[a] >= 15 || POINT_BUY_COST[scores[a] + 1] - POINT_BUY_COST[scores[a]] > budgetLeft} onClick={() => bump(a, 1)} className="w-7 h-7 rounded-full border border-white/10 disabled:opacity-30 hover:border-[#D4AF37]">+</button>
          </div>
        ))}
      </div>
      <Button data-testid="point-buy-continue-button" className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f]" onClick={() => onDone(scores)}>Continue</Button>
    </div>
  );
}

export default function AbilityScoreWizardDnd5e({ open, onOpenChange, use2024Rules, onComplete }) {
  const [phase, setPhase] = useState("method");
  const [method, setMethod] = useState(null);
  const [rolledPool, setRolledPool] = useState([]);
  const [baseScores, setBaseScores] = useState(null);
  const [races, setRaces] = useState([]);
  const [backgrounds, setBackgrounds] = useState([]);
  const [selectedRace, setSelectedRace] = useState(null);
  const [selectedBg, setSelectedBg] = useState(null);
  const [raceChoicePicks, setRaceChoicePicks] = useState([]);
  const [bgSplitMode, setBgSplitMode] = useState(null); // "split" | "even"
  const [bgSplitPlus2, setBgSplitPlus2] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPhase("method");
    setMethod(null);
    setBaseScores(null);
    setSelectedRace(null);
    setSelectedBg(null);
    setRaceChoicePicks([]);
    setBgSplitMode(null);
    setBgSplitPlus2(null);
    api.get("/dnd5e/races").then((r) => setRaces(r.data)).catch(() => setRaces([]));
    api.get("/dnd5e/backgrounds").then((r) => setBackgrounds(r.data)).catch(() => setBackgrounds([]));
  }, [open]);

  if (!open) return null;

  const finishWithRaceAndBg = (scores, race, bg, opts = {}) => {
    let final = { ...scores };
    const history = ABILITIES.map((a) => ({ source: "base", ability: a, amount: scores[a] }));
    if (!use2024Rules && race) {
      for (const [k, v] of Object.entries(race.ability_bonuses.fixed || {})) {
        final[k] = final[k] + v;
        history.push({ source: "race", ability: k, amount: v });
      }
      const picks = opts.raceChoices || raceChoicePicks;
      for (const a of picks) {
        final[a] = final[a] + (race.ability_bonuses.choice_amount || 1);
        history.push({ source: "race_choice", ability: a, amount: race.ability_bonuses.choice_amount || 1 });
      }
    }
    if (use2024Rules && bg) {
      const bgMode = opts.bgMode || bgSplitMode;
      if (bgMode === "even") {
        for (const a of bg.ability_options) {
          final[a] = final[a] + 1;
          history.push({ source: "background", ability: a, amount: 1 });
        }
      } else if (bgMode === "split") {
        const plus2 = opts.bgPlus2 || bgSplitPlus2?.plus2;
        const plus1 = opts.bgPlus1 || bgSplitPlus2?.plus1;
        if (plus2) { final[plus2] = final[plus2] + 2; history.push({ source: "background", ability: plus2, amount: 2 }); }
        if (plus1) { final[plus1] = final[plus1] + 1; history.push({ source: "background", ability: plus1, amount: 1 }); }
      }
    }
    onComplete({ abilities: final, raceName: race?.name || "", backgroundName: bg?.name || "", boostHistory: history });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#322216] border-white/10 text-[#f1e0c5] max-w-md max-h-[85vh] overflow-y-auto scrollbar-thin" data-testid="ability-score-wizard-dnd5e">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Ability Scores</DialogTitle>
          <DialogDescription>{use2024Rules ? "2024 rules — your background grants the ability bonus" : "2014 rules — your race grants the ability bonus"}</DialogDescription>
        </DialogHeader>
        {baseScores && <ScoreTable abilities={baseScores} />}

        {phase === "method" && (
          <div className="space-y-2">
            <p className="text-sm text-[#f1e0c5]/70">How do you want to generate your base scores?</p>
            <Button data-testid="method-standard-array-button" className="w-full rounded-full justify-start" variant="secondary" onClick={() => { setMethod("standard"); setPhase("assign"); }}>Standard Array (15, 14, 13, 12, 10, 8)</Button>
            <Button data-testid="method-point-buy-button" className="w-full rounded-full justify-start" variant="secondary" onClick={() => { setMethod("point-buy"); setPhase("assign"); }}>Point Buy (27 points)</Button>
            <Button data-testid="method-roll-button" className="w-full rounded-full justify-start" variant="secondary" onClick={() => { const pool = Array.from({ length: 6 }, roll4d6DropLowest); setRolledPool(pool); setMethod("roll"); setPhase("assign"); }}>Roll (4d6, drop lowest × 6)</Button>
          </div>
        )}

        {phase === "assign" && method === "point-buy" && (
          <PointBuyStep onDone={(scores) => { setBaseScores(scores); setPhase(use2024Rules ? "background" : "race"); }} />
        )}
        {phase === "assign" && method === "standard" && (
          <AssignPoolStep pool={STANDARD_ARRAY} onDone={(scores) => { setBaseScores(scores); setPhase(use2024Rules ? "background" : "race"); }} />
        )}
        {phase === "assign" && method === "roll" && (
          <AssignPoolStep pool={rolledPool} onDone={(scores) => { setBaseScores(scores); setPhase(use2024Rules ? "background" : "race"); }} />
        )}

        {phase === "race" && (
          <div className="space-y-2">
            <p className="text-sm text-[#f1e0c5]/70">Pick a race — its ability bonus applies automatically.</p>
            <div className="grid grid-cols-1 gap-1.5 max-h-64 overflow-y-auto scrollbar-thin">
              {races.map((r) => (
                <button
                  key={r.slug}
                  type="button"
                  data-testid={`race-pick-${r.slug}`}
                  onClick={() => {
                    setSelectedRace(r);
                    if ((r.ability_bonuses.choice_count || 0) > 0) setPhase("race-choice");
                    else finishWithRaceAndBg(baseScores, r, null);
                  }}
                  className="text-left text-xs px-3 py-2 rounded-lg border border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-200"
                >
                  <span className="font-semibold">{r.name}</span>
                  <span className="text-[#f1e0c5]/40"> — {Object.entries(r.ability_bonuses.fixed).map(([k, v]) => `+${v} ${k.toUpperCase()}`).join(", ")}{r.ability_bonuses.choice_count ? `, +${r.ability_bonuses.choice_amount} to ${r.ability_bonuses.choice_count} of your choice` : ""}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {phase === "race-choice" && selectedRace && (
          <div className="space-y-2">
            <p className="text-sm text-[#f1e0c5]/70">Choose {selectedRace.ability_bonuses.choice_count} abilities (besides {(selectedRace.ability_bonuses.choice_exclude || []).map((a) => ABILITY_LABELS[a]).join(", ")}) to each get +{selectedRace.ability_bonuses.choice_amount}.</p>
            <div className="grid grid-cols-3 gap-2">
              {ABILITIES.filter((a) => !(selectedRace.ability_bonuses.choice_exclude || []).includes(a)).map((a) => (
                <button
                  key={a}
                  type="button"
                  data-testid={`race-choice-pick-${a}`}
                  disabled={raceChoicePicks.includes(a) || raceChoicePicks.length >= selectedRace.ability_bonuses.choice_count}
                  onClick={() => {
                    const next = [...raceChoicePicks, a];
                    setRaceChoicePicks(next);
                    if (next.length >= selectedRace.ability_bonuses.choice_count) finishWithRaceAndBg(baseScores, selectedRace, null, { raceChoices: next });
                  }}
                  className={`text-xs px-2 py-2 rounded-lg border transition-colors duration-200 ${raceChoicePicks.includes(a) ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37]"} disabled:opacity-30`}
                >
                  {ABILITY_LABELS[a]}
                </button>
              ))}
            </div>
          </div>
        )}

        {phase === "background" && (
          <div className="space-y-2">
            <p className="text-sm text-[#f1e0c5]/70">Pick a background — its three abilities can take a bonus.</p>
            <div className="grid grid-cols-1 gap-1.5 max-h-64 overflow-y-auto scrollbar-thin">
              {backgrounds.map((b) => (
                <button
                  key={b.slug}
                  type="button"
                  data-testid={`background-pick-${b.slug}`}
                  onClick={() => { setSelectedBg(b); setPhase("background-split"); }}
                  className="text-left text-xs px-3 py-2 rounded-lg border border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-200"
                >
                  <span className="font-semibold">{b.name}</span>
                  <span className="text-[#f1e0c5]/40"> — {b.ability_options.map((a) => ABILITY_LABELS[a]).join(", ")}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {phase === "background-split" && selectedBg && (
          <div className="space-y-3">
            <p className="text-sm text-[#f1e0c5]/70">Split +2/+1 across two of these three abilities, or +1 to all three.</p>
            {!bgSplitMode && (
              <div className="flex gap-2">
                <Button data-testid="bg-split-mode-even-button" variant="secondary" className="flex-1 rounded-full" onClick={() => { setBgSplitMode("even"); finishWithRaceAndBg(baseScores, null, selectedBg, { bgMode: "even" }); }}>+1 to All Three</Button>
                <Button data-testid="bg-split-mode-split-button" variant="secondary" className="flex-1 rounded-full" onClick={() => setBgSplitMode("split")}>+2/+1 Split</Button>
              </div>
            )}
            {bgSplitMode === "split" && !bgSplitPlus2 && (
              <div>
                <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1">Choose the ability that gets +2</p>
                <div className="grid grid-cols-3 gap-2">
                  {selectedBg.ability_options.map((a) => (
                    <button key={a} type="button" data-testid={`bg-plus2-pick-${a}`} onClick={() => setBgSplitPlus2({ plus2: a, plus1: null })} className="text-xs px-2 py-2 rounded-lg border border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37]">{ABILITY_LABELS[a]}</button>
                  ))}
                </div>
              </div>
            )}
            {bgSplitMode === "split" && bgSplitPlus2 && !bgSplitPlus2.plus1 && (
              <div>
                <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1">Choose the ability that gets +1</p>
                <div className="grid grid-cols-3 gap-2">
                  {selectedBg.ability_options.filter((a) => a !== bgSplitPlus2.plus2).map((a) => (
                    <button
                      key={a}
                      type="button"
                      data-testid={`bg-plus1-pick-${a}`}
                      onClick={() => { const picks = { plus2: bgSplitPlus2.plus2, plus1: a }; setBgSplitPlus2(picks); finishWithRaceAndBg(baseScores, null, selectedBg, { bgMode: "split", bgPlus2: bgSplitPlus2.plus2, bgPlus1: a }); }}
                      className="text-xs px-2 py-2 rounded-lg border border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                    >
                      {ABILITY_LABELS[a]}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
